import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Text } from 'designSystem/components/Text'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import type { DocChatQueuedMessage } from 'features/knowledgeDocumentViewer/types'
import React from 'react'

import { Messages } from './docChatMessageQueue.styles'
import { DocChatQueuedMessageRow } from './DocChatQueuedMessageRow'

export function DocChatMessageQueue() {
  const { searchState, siteId, onSearchStateChange } = useAdvancedSearchUX()
  const { t } = useLingui()
  const messages =
    searchState.docChat?.siteId === siteId
      ? (searchState.docChat.queuedMessages ?? [])
      : []

  const updateMessage = (
    id: string,
    update: (message: DocChatQueuedMessage) => DocChatQueuedMessage | null
  ) => {
    onSearchStateChange((current) => {
      const docChat = current.docChat
      if (docChat?.siteId !== siteId) return {}
      return {
        docChat: {
          ...docChat,
          queuedMessages: docChat.queuedMessages?.flatMap((message) => {
            if (message.id !== id) return [message]
            const next = update(message)
            return next ? [next] : []
          }),
        },
      }
    })
  }
  const handleEdit = (id: string) =>
    updateMessage(id, (message) => ({ ...message, isEditing: true }))
  const handleSave = (id: string, query: string) =>
    updateMessage(id, (message) => ({ ...message, query, isEditing: false }))
  const handleCancelEdit = (id: string) =>
    updateMessage(id, (message) => ({ ...message, isEditing: false }))
  const handleRemove = (id: string) => updateMessage(id, () => null)

  if (!messages.length) return null
  return (
    <Box asChild display='flex' direction='column' gap='xsPlus' width='full'>
      <section aria-label={t`Queued messages`}>
        <Text variant='caption' color='muted'>
          {messages.some((message) => message.isEditing) ? (
            <Trans>Queue paused while editing.</Trans>
          ) : (
            <Trans>
              Queued messages send in order after the current response.
            </Trans>
          )}
        </Text>
        <Box
          asChild
          display='flex'
          direction='column'
          gap='xsPlus'
          overflow='yAuto'
          padding='none'
          margin='none'>
          <Messages>
            {messages.map((message, index) => (
              <DocChatQueuedMessageRow
                key={message.id}
                message={message}
                position={index + 1}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancelEdit={handleCancelEdit}
                onRemove={handleRemove}
              />
            ))}
          </Messages>
        </Box>
      </section>
    </Box>
  )
}
